import Big from 'big.js'
import { Measure } from '../types'


enum BandwidthMeasurementSystem {
  bitBasedSI = 'Bit-based SI Units (1000)',
  bitBasedBinary = 'Bit-based Binary Units (1024)',
  byteBasedSI = 'Byte-based SI Units (1000)',
  byteBasedBinary = 'Byte-based Binary Units (1024)'
}


const bandwidthUnits = {
  // BIT PER SECOND
  bit_per_second: {
    codeName: 'bit_per_second',
    displayName: 'Bit per second',
    url: 'bit-per-second',
    symbol: 'bps',
    toBaseUnit: (value: Big) => value.div(1000000),
    fromBaseUnit: (value: Big) => value.times(1000000),
    system: BandwidthMeasurementSystem.bitBasedSI
  },
  kilobit_per_second: {
    codeName: 'kilobit_per_second',
    displayName: 'Kilobit per second',
    url: 'kilobit-per-second',
    symbol: 'Kbps',
    toBaseUnit: (value: Big) => value.div(1000),
    fromBaseUnit: (value: Big) => value.times(1000),
    system: BandwidthMeasurementSystem.bitBasedSI
  },
  megabit_per_second: {
    codeName: 'megabit_per_second',
    displayName: 'Megabit per second',
    url: 'megabit-per-second',
    symbol: 'Mbps',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: BandwidthMeasurementSystem.bitBasedSI
  },
  gigabit_per_second: {
    codeName: 'gigabit_per_second',
    displayName: 'Gigabit per second',
    url: 'gigabit-per-second',
    symbol: 'Gbps',
    toBaseUnit: (value: Big) => value.times(1000),
    fromBaseUnit: (value: Big) => value.div(1000),
    system: BandwidthMeasurementSystem.bitBasedSI
  },
  terabit_per_second: {
    codeName: 'terabit_per_second',
    displayName: 'Terabit per second',
    url: 'terabit-per-second',
    symbol: 'Tbps',
    toBaseUnit: (value: Big) => value.times(1000000),
    fromBaseUnit: (value: Big) => value.div(1000000),
    system: BandwidthMeasurementSystem.bitBasedSI
  },


  // BYTE PER SECOND
  byte_per_second: {
    codeName: 'byte_per_second',
    displayName: 'Byte per second',
    url: 'byte-per-second',
    symbol: 'Bps',
    toBaseUnit: (value: Big) => value.times(8).div(1000000),
    fromBaseUnit: (value: Big) => value.times(1000000).div(8),
    system: BandwidthMeasurementSystem.byteBasedSI
  },
  kilobyte_per_second: {
    codeName: 'kilobyte_per_second',
    displayName: 'Kilobyte per second',
    url: 'kilobyte-per-second',
    symbol: 'KBps',
    toBaseUnit: (value: Big) => value.times(8).div(1000),
    fromBaseUnit: (value: Big) => value.times(1000).div(8),
    system: BandwidthMeasurementSystem.byteBasedSI
  },
  megabyte_per_second: {
    codeName: 'megabyte_per_second',
    displayName: 'Megabyte per second',
    url: 'megabyte-per-second',
    symbol: 'MBps',
    toBaseUnit: (value: Big) => value.times(8),
    fromBaseUnit: (value: Big) => value.div(8),
    system: BandwidthMeasurementSystem.byteBasedSI
  },
  gigabyte_per_second: {
    codeName: 'gigabyte_per_second',
    displayName: 'Gigabyte per second',
    url: 'gigabyte-per-second',
    symbol: 'GBps',
    toBaseUnit: (value: Big) => value.times(8).times(1000),
    fromBaseUnit: (value: Big) => value.div(8).div(1000),
    system: BandwidthMeasurementSystem.byteBasedSI
  },
  terabyte_per_second: {
    codeName: 'terabyte_per_second',
    displayName: 'Terabyte per second',
    url: 'terabyte-per-second',
    symbol: 'TBps',
    toBaseUnit: (value: Big) => value.times(8).times(1000000),
    fromBaseUnit: (value: Big) => value.div(8).div(1000000),
    system: BandwidthMeasurementSystem.byteBasedSI
  },


  // BIBIT PER SECOND
  kibibit_per_second: {
    codeName: 'kibibit_per_second',
    displayName: 'Kibibit per second',
    url: 'kibibit-per-second',
    symbol: 'Kibps',
    toBaseUnit: (value: Big) => value.times(1024).div(1000000),
    fromBaseUnit: (value: Big) => value.times(1000000).div(1024),
    system: BandwidthMeasurementSystem.bitBasedBinary
  },
  mebibit_per_second: {
    codeName: 'mebibit_per_second',
    displayName: 'Mebibit per second',
    url: 'mebibit-per-second',
    symbol: 'Mibps',
    toBaseUnit: (value: Big) => value.times(1024 * 1024).div(1000000),
    fromBaseUnit: (value: Big) => value.times(1000000).div(1024 * 1024),
    system: BandwidthMeasurementSystem.bitBasedBinary
  },
  gibibit_per_second: {
    codeName: 'gibibit_per_second',
    displayName: 'Gibibit per second',
    url: 'gibibit-per-second',
    symbol: 'Gibps',
    toBaseUnit: (value: Big) => value.times(1024 * 1024 * 1024).div(1000000),
    fromBaseUnit: (value: Big) => value.times(1000000).div(1024 * 1024 * 1024),
    system: BandwidthMeasurementSystem.bitBasedBinary
  },
  tebibit_per_second: {
    codeName: 'tebibit_per_second',
    displayName: 'Tebibit per second',
    url: 'tebibit-per-second',
    symbol: 'Tibps',
    toBaseUnit: (value: Big) => value.times(1024 * 1024 * 1024 * 1024).div(1000000),
    fromBaseUnit: (value: Big) => value.times(1000000).div(1024 * 1024 * 1024 * 1024),
    system: BandwidthMeasurementSystem.bitBasedBinary
  },

  // BIBYTE PER SECOND
  kibibyte_per_second: {
    codeName: 'kibibyte_per_second',
    displayName: 'Kibibyte per second',
    url: 'kibibyte-per-second',
    symbol: 'KiBps',
    toBaseUnit: (value: Big) => value.times(8).times(1024).div(1000000),
    fromBaseUnit: (value: Big) => value.div(8).times(1000000).div(1024),
    system: BandwidthMeasurementSystem.byteBasedBinary
  },
  mebibyte_per_second: {
    codeName: 'mebibyte_per_second',
    displayName: 'Mebibyte per second',
    url: 'mebibyte-per-second',
    symbol: 'MiBps',
    toBaseUnit: (value: Big) => value.times(8).times(1024 * 1024).div(1000000),
    fromBaseUnit: (value: Big) => value.div(8).times(1000000).div(1024 * 1024),
    system: BandwidthMeasurementSystem.byteBasedBinary
  },
  gibibyte_per_second: {
    codeName: 'gibibyte_per_second',
    displayName: 'Gibibyte per second',
    url: 'gibibyte-per-second',
    symbol: 'GiB/s',
    toBaseUnit: (value: Big) => value.times(8).times(1024).times(1024).times(1024).div(1000 * 1000),
    fromBaseUnit: (value: Big) => value.times(1000 * 1000).div(8 * (1024 * 1024 * 1024)),
    system: BandwidthMeasurementSystem.byteBasedBinary
  },
  tebibyte_per_second: {
    codeName: 'tebibyte_per_second',
    displayName: 'Tebibyte per second',
    url: 'tebibyte-per-second',
    symbol: 'TiB/s',
    toBaseUnit: (value: Big) => value.times(8).times(1024).times(1024).times(1024).times(1024).div(1000 * 1000),
    fromBaseUnit: (value: Big) => value.times(1000 * 1000).div(8 * (1024 * 1024 * 1024 * 1024)),
    system: BandwidthMeasurementSystem.byteBasedBinary
  }
  // todo: add transfer durations units https://www.unitjuggler.com/convert-bandwidth-from-Mbps-to-Tibps.html
}

export const bandwidth: Measure = {
  displayName: 'Bandwidth',
  codeName: 'bandwidth',
  url: 'bandwidth',
  units: bandwidthUnits,
  baseUnit: bandwidthUnits.megabit_per_second,
  defaultFromUnit: bandwidthUnits.megabyte_per_second,
  defaultToUnit: bandwidthUnits.megabit_per_second
}
